import React from "react";
import { useNavigate } from "react-router-dom";
import { formatEur, activityFromPrice } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { AgeBadges } from "@/lib/ageCategories";
import { MapPin, Clock, Info } from "lucide-react";

function fmtDateRange(a) {
  const s = a.start_date ? new Date(a.start_date + "T00:00:00") : null;
  const opt = { day: "numeric", month: "long" };
  if (!s) return "";
  if (a.end_date && a.end_date !== a.start_date) {
    const e = new Date(a.end_date + "T00:00:00");
    return `${s.toLocaleDateString("es-ES", opt)} – ${e.toLocaleDateString("es-ES", opt)}`;
  }
  return s.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
}

export function ActivityCard({ a }) {
  const navigate = useNavigate();
  const canSignup = a.status !== "cancelada" && !a.inscriptions_closed && !a.is_full;
  const minPrice = activityFromPrice(a);
  const go = () => navigate(`/actividad/${a.id}`);
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div data-testid={`activity-card-${a.id}`} onClick={go} className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 transition-transform hover:-translate-y-1 duration-200 flex flex-col cursor-pointer">
      <div className="relative h-48 overflow-hidden">
        <img src={a.photos?.[0]} alt={a.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute top-3 left-3"><StatusBadge activity={a} /></div>
        {a.activity_type && <span className="absolute top-3 right-3 bg-white/90 text-[#003284] text-[11px] font-bold px-2.5 py-1 rounded-full">{a.activity_type}</span>}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs font-bold text-[#FF7F27] uppercase tracking-wide">{fmtDateRange(a)}</p>
        <h3 className="text-lg font-black brand-navy mt-1 leading-tight">{a.title}</h3>
        <div className="mt-2"><AgeBadges ids={a.age_categories} /></div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
          <span className="flex items-center gap-1"><Clock size={13} />{a.schedule}</span>
          <span className="flex items-center gap-1"><MapPin size={13} />{a.place}</span>
        </div>
        <p className="text-sm text-slate-600 mt-3 line-clamp-4 leading-relaxed flex-1">{a.short_description}</p>
        <div className="flex items-center justify-between mt-4">
          <div>
            <span className="text-xs text-slate-400">Desde</span>
            <p className="text-xl font-black brand-navy">{minPrice != null ? formatEur(minPrice) : "Consultar"}</p>
          </div>
          {a.is_full && <span className="text-xs font-bold text-slate-600">Completa · lista de espera</span>}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button data-testid={`more-info-${a.id}`} onClick={stop(() => navigate(`/actividad/${a.id}`))} className="flex items-center justify-center gap-1.5 border-2 border-[#003284] text-[#003284] font-bold rounded-full py-2.5 text-sm hover:bg-[#003284] hover:text-white transition-colors">
            <Info size={15} /> Más info
          </button>
          <button data-testid={`signup-${a.id}`} disabled={!canSignup} onClick={stop(() => navigate(`/actividad/${a.id}/apuntarse`))} className="bg-brand-orange text-white font-bold rounded-full py-2.5 text-sm hover:bg-[#DE7A1C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {a.is_full ? "Lista de espera" : "Apuntarse"}
          </button>
        </div>
      </div>
    </div>
  );
}
